import { ChevronDown, ChevronRight, FileDown, GraduationCap, Plus, Scale, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, tokens } from '../lib/api';
import { gradePct, overallPct } from '../lib/grades';
import { ASSIGNMENT_CATEGORIES, DEFAULT_SCALE, letterFor, type Assignment, type Child, type GradeBand } from '../lib/types';
import { Sheet } from '../ui/Sheet';
import { SubjectField } from '../ui/SubjectField';
import { useParent } from './ParentContext';
import { childHas } from './Plans';
import { WorkSamples } from './WorkSamples';
import { SchoolBanner, useSchoolLink } from './SchoolLink';

const pctText = (pct: number | null) => (pct === null ? '—' : `${Math.round(pct)}%`);

/** Today as local 'YYYY-MM-DD'. */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** 'YYYY-MM-DD' as "Sep 10". */
const shortDay = (ymd: string) => new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** The Grades tab: assignments per subject with a computed, weighted grade (#97). */
export function GradeBook({ child }: { child: Child }) {
  const { toast } = useParent();
  const [items, setItems] = useState<Assignment[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [editingScale, setEditingScale] = useState(false);
  const letter = (pct: number) => letterFor(child.gradingScale, pct);

  // Connexus grades read by the browser extension (#654), and each course's grade as Connexus shows it.
  const { info: school, setInfo: setSchool } = useSchoolLink(child.id);
  const schoolGrade = (subject: string) => school?.courses.find((c) => c.name === subject);

  // Which subjects are open (#665), remembered per child in this browser.
  const openKey = `cg.gradebook.open.${child.id}`;
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(openKey) || '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const setOpenSubjects = (list: string[]) => {
    const next = new Set(list);
    setOpen(next);
    try {
      localStorage.setItem(openKey, JSON.stringify([...next]));
    } catch {
      /* storage unavailable */
    }
  };
  const toggle = (subject: string) => setOpenSubjects(open.has(subject) ? [...open].filter((x) => x !== subject) : [...open, subject]);
  const subjectAnchor = (subject: string) => `gb-${subject.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const showSubject = (subject: string) => {
    if (!open.has(subject)) setOpenSubjects([...open, subject]);
    requestAnimationFrame(() => document.getElementById(subjectAnchor(subject))?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  // Connexus's own Overall (#823): it wins over the average of the subjects, which matches each course but not always it.
  const [schoolOverall, setSchoolOverall] = useState<number | null>(null);
  const load = useCallback(() => {
    api.get<Assignment[]>(`/api/children/${child.id}/assignments`).then(setItems).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
    api
      .get<{ overall?: number | null }>(`/api/children/${child.id}/school-link`)
      .then((r) => setSchoolOverall(r.overall ?? null))
      .catch(() => setSchoolOverall(null));
  }, [child.id, toast]);
  useEffect(load, [load]);

  if (!items) return <p className="muted">Loading…</p>;

  const subjects = [...new Set(items.map((a) => a.subject))].sort((a, b) => a.localeCompare(b));
  const overall = schoolOverall ?? overallPct(items);

  return (
    <section className="card stack">
      <div className="row-between">
        <h2 className="row-gap">
          <GraduationCap size={18} /> Grades for {child.name}
        </h2>
        <span className="row-gap">
          {overall !== null && (
            <span className="grade-overall">
              {pctText(overall)} <span className="grade-letter">{letter(overall)}</span>
            </span>
          )}
          <button className="btn small" onClick={() => setEditingScale(true)}>
            <Scale size={15} /> Grading scale
          </button>
          {childHas(child, 'report_cards') && (
            <button className="btn small" onClick={() => setReporting(true)}>
              <FileDown size={15} /> Download report
            </button>
          )}
        </span>
      </div>
      {reporting && <ReportSheet child={child} onClose={() => setReporting(false)} />}
      {editingScale && <ScaleSheet child={child} onClose={() => setEditingScale(false)} />}

      <SchoolBanner
        child={child}
        info={school}
        part="grades"
        onUpdated={(i) => {
          setSchool(i);
          load();
        }}
      />
      {items.length === 0 && <p className="muted small">No assignments yet. Add one to start tracking grades.</p>}

      {/* At a glance (#665): every subject's grade; tapping one opens it below. */}
      {subjects.length > 1 && (
        <div className="grade-tiles">
          {subjects.map((subject) => {
            const rows = items.filter((a) => a.subject === subject);
            const pct = gradePct(rows);
            const cx = schoolGrade(subject);
            return (
              <button key={subject} type="button" className={`grade-tile${open.has(subject) ? ' open' : ''}`} onClick={() => showSubject(subject)}>
                <span className="grade-tile-name">{subject}</span>
                <span className="grade-tile-pct">
                  {pct !== null ? pctText(pct) : '—'} {pct !== null && <span className="grade-letter">{letter(pct)}</span>}
                </span>
                <span className="muted small">
                  {rows.length} graded
                  {cx && <span className="tag school-tag">Connexus {cx.grade || cx.score}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {subjects.length > 1 && (
        <div className="row-between">
          <strong>By subject</strong>
          <span className="row-gap">
            <button type="button" className="link-btn small" onClick={() => setOpenSubjects(subjects)}>
              Open all
            </button>
            <button type="button" className="link-btn small" onClick={() => setOpenSubjects([])}>
              Close all
            </button>
          </span>
        </div>
      )}

      {subjects.map((subject) => {
        const rows = items.filter((a) => a.subject === subject);
        const pct = gradePct(rows);
        // One subject on its own stays open; with several, each starts closed to its name and grade (#665).
        const isOpen = subjects.length === 1 || open.has(subject);
        return (
          <div key={subject} id={subjectAnchor(subject)} className="stack sub-section">
            <button type="button" className="grade-subject row-between" onClick={() => toggle(subject)} aria-expanded={isOpen}>
              <span className="row-gap">
                {subjects.length > 1 && (isOpen ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />)}
                <h3>{subject}</h3>
                <span className="muted small">· {rows.length} graded</span>
              </span>
              <span className="row-gap">
                {schoolGrade(subject) && (
                  <span className="tag school-tag" title="The course grade as Connexus shows it">
                    Connexus {schoolGrade(subject)!.grade || schoolGrade(subject)!.score}
                  </span>
                )}
                {pct !== null && (
                  <strong>
                    {pctText(pct)} <span className="grade-letter">{letter(pct)}</span>
                  </strong>
                )}
              </span>
            </button>
            {isOpen && rows.map((a) => <AssignmentRow key={a.id} child={child} item={a} reload={load} />)}
          </div>
        );
      })}

      {adding ? (
        <AssignmentForm child={child} childId={child.id} subjects={subjects} onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      ) : (
        <button className="btn" onClick={() => setAdding(true)}>
          <Plus size={16} /> Add assignment
        </button>
      )}
    </section>
  );
}

function AssignmentRow({ child, item, reload }: { child: Child; item: Assignment; reload: () => void }) {
  const { toast } = useParent();
  const [editing, setEditing] = useState(false);
  const pct = item.pointsAvailable > 0 ? (item.pointsEarned / item.pointsAvailable) * 100 : null;

  async function remove() {
    const own = !item.plannerItemId && item.samples ? ` Its ${item.samples === 1 ? 'work sample goes' : `${item.samples} work samples go`} too.` : '';
    if (!confirm(`Delete “${item.name}”?${own}`)) return;
    await api.del(`/api/assignments/${item.id}`).then(reload).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }

  if (editing) return <AssignmentForm child={child} item={item} onDone={() => { setEditing(false); reload(); }} onCancel={() => setEditing(false)} />;

  return (
    <div className="row-between list-row">
      <span className="grow">
        <strong>{item.name}</strong>
        {item.category && (
          <span className={`tag${item.category === PORTFOLIO ? ' portfolio-tag' : ''}`} style={{ marginLeft: 6 }}>
            {item.category}
            {item.category === PORTFOLIO && item.samples ? ` · ${item.samples}` : ''}
          </span>
        )}
        {item.fromSchool && (
          <span className="tag school-tag" style={{ marginLeft: 6 }} title="Read from Connexus. It changes there, not here.">
            Connexus
          </span>
        )}
        {item.fromPlanner && (
          <span className="tag" style={{ marginLeft: 6 }} title="Added from the planner (Is graded)">
            Planner
          </span>
        )}
        <span className="muted small block">
          {item.date && `${shortDay(item.date)} · `}
          {item.pointsAvailable > 0
            ? `${item.pointsEarned}/${item.pointsAvailable} pts${pct !== null ? ` · ${Math.round(pct)}%` : ''} · weight ${item.weight}`
            : 'Not scored yet — Edit to add the score'}
        </span>
      </span>
      {/* A Connexus grade (#654) changes there, so it isn't edited or deleted here. */}
      {!item.fromSchool && (
        <span className="row-gap">
          <button className="btn small" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn small danger-text" onClick={remove} title="Delete">
            <Trash2 size={14} />
          </button>
        </span>
      )}
    </div>
  );
}

function AssignmentForm({
  child,
  childId,
  item,
  subjects = [],
  onDone,
  onCancel,
}: {
  child: Child;
  childId?: string;
  item?: Assignment;
  subjects?: string[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { toast } = useParent();
  const [subject, setSubject] = useState(item?.subject ?? '');
  const [name, setName] = useState(item?.name ?? '');
  const [earned, setEarned] = useState(item ? String(item.pointsEarned) : '');
  const [available, setAvailable] = useState(item ? String(item.pointsAvailable) : '');
  const [weight, setWeight] = useState(item ? String(item.weight) : '1');
  const [date, setDate] = useState(item?.date || today());
  const [category, setCategory] = useState(item?.category ?? '');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const payload = {
        subject: subject.trim(),
        name: name.trim(),
        pointsEarned: Number(earned) || 0,
        pointsAvailable: Number(available) || 0,
        weight: Number(weight) || 0,
        ...(date ? { date } : {}),
        category,
      };
      if (item) await api.patch(`/api/assignments/${item.id}`, payload);
      else await api.post(`/api/children/${childId}/assignments`, payload);
      onDone();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack assignment-form">
      <div className="row-between">
        <strong>{item ? 'Edit assignment' : 'New assignment'}</strong>
        <button className="icon-btn" onClick={onCancel} aria-label="Cancel">
          <X size={16} />
        </button>
      </div>
      <div className="field-row">
        <label className="grow">
          Subject
          <SubjectField value={subject} onChange={setSubject} extra={subjects} />
        </label>
        <label>
          Category
          <CategorySelect value={category} onChange={setCategory} />
        </label>
      </div>
      <div className="field-row">
        <label className="grow">
          Assignment name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="e.g. Chapter 3 quiz" />
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      <div className="field-row">
        <label>
          Points earned
          <input type="number" min={0} step="any" value={earned} onChange={(e) => setEarned(e.target.value)} />
        </label>
        <label>
          Points available
          <input type="number" min={0} step="any" value={available} onChange={(e) => setAvailable(e.target.value)} />
        </label>
        <label>
          Weight
          <input type="number" min={0} step="any" value={weight} onChange={(e) => setWeight(e.target.value)} />
        </label>
      </div>
      {category === PORTFOLIO && <PortfolioWork child={child} plannerItemId={item?.plannerItemId ?? undefined} assignmentId={item && !item.plannerItemId ? item.id : undefined} />}
      <button className="btn primary" onClick={submit} disabled={busy || !subject.trim() || !name.trim()}>
        {item ? 'Save' : 'Add'}
      </button>
    </div>
  );
}

type ReportKind = 'card' | 'progress';
type Range = 'month' | 'year' | 'custom';

/**
 * Download a report card or a progress report as a Word document (#254). The report card covers the school year; a
 * progress report covers a month, the school year or custom dates.
 */
function ReportSheet({ child, onClose }: { child: Child; onClose: () => void }) {
  const { toast } = useParent();
  const year = child.schoolYear ?? null;
  const [kind, setKind] = useState<ReportKind>('card');
  const [range, setRange] = useState<Range>('month');
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [from, setFrom] = useState(() => `${today().slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  // Report card periods (#299): '' = the school year so far, or a period's index once it has ended.
  const [periods, setPeriods] = useState<ReportPeriodView[]>([]);
  const [period, setPeriod] = useState('');
  useEffect(() => {
    api
      .get<{ periods: ReportPeriodView[] }>('/api/report-periods')
      .then((r) => {
        setPeriods(r.periods);
        // Default to the latest period that's ready.
        const ready = r.periods.map((p, i) => (p.available ? i : -1)).filter((i) => i >= 0);
        if (ready.length) setPeriod(String(ready[ready.length - 1]));
      })
      .catch(() => undefined);
  }, []);

  // The progress report's dates for the chosen range.
  const dates = (): { from: string; to: string } => {
    if (range === 'year' && year) return { from: year.start, to: year.end };
    if (range === 'month') {
      const [y, m] = month.split('-').map(Number);
      const last = new Date(y!, m!, 0).getDate();
      return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
    }
    return { from, to };
  };
  const bad = kind === 'progress' && (range === 'custom' ? !from || !to || from > to : range === 'month' ? !month : false);

  async function download() {
    setBusy(true);
    try {
      const path = kind === 'card' ? 'report-card' : 'progress-report';
      const body = kind === 'card' ? { comments, ...(period !== '' ? { period: Number(period) } : {}) } : { ...dates(), comments };
      const res = await fetch(`/api/children/${child.id}/${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${tokens.parent()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: string } | null)?.error || 'Could not make the report.');
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('content-disposition')?.match(/filename="(.+?)"/)?.[1] || `${path}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ text: kind === 'card' ? 'Report card downloaded.' : 'Progress report downloaded.', tone: 'good' });
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Download a report" onClose={onClose}>
      <div className="stack">
        <div className="segmented" role="radiogroup" aria-label="Report">
          <button role="radio" aria-checked={kind === 'card'} className={kind === 'card' ? 'active' : ''} onClick={() => setKind('card')}>
            Report card
          </button>
          <button role="radio" aria-checked={kind === 'progress'} className={kind === 'progress' ? 'active' : ''} onClick={() => setKind('progress')}>
            Progress report
          </button>
        </div>
        {kind === 'card' && periods.length > 0 && (
          <label>
            Report card for
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              {periods.map((p, i) => (
                <option key={i} value={String(i)} disabled={!p.available}>
                  {p.name} ({shortDay(p.start)} – {shortDay(p.end)}){p.available ? '' : ` · available after ${shortDay(p.end)}`}
                </option>
              ))}
              <option value="">The school year so far</option>
            </select>
          </label>
        )}
        {kind === 'card' ? (
          <p className="muted small">
            Each subject’s average and letter grade, the overall grade and attendance
            {period !== '' && periods[Number(period)] ? (
              <>
                {' '}
                for {periods[Number(period)]!.name} ({shortDay(periods[Number(period)]!.start)} – {shortDay(periods[Number(period)]!.end)}).
              </>
            ) : year ? (
              <>
                {' '}
                for the school year ({shortDay(year.start)} – {shortDay(year.end)}).
              </>
            ) : (
              <> for every grade so far. Set the school year in Settings → School Settings to limit it to this year.</>
            )}
          </p>
        ) : (
          <>
            <p className="muted small">Subject averages plus every assignment and score in the period, with attendance.</p>
            <div className="segmented" role="radiogroup" aria-label="Period">
              {(
                [
                  ['month', 'Month'],
                  ['year', 'School year'],
                  ['custom', 'Custom'],
                ] as [Range, string][]
              ).map(([id, label]) => (
                <button
                  key={id}
                  role="radio"
                  aria-checked={range === id}
                  className={range === id ? 'active' : ''}
                  onClick={() => setRange(id)}
                  disabled={id === 'year' && !year}
                  title={id === 'year' && !year ? 'Set the school year in Settings → School Settings to use this' : undefined}
                >
                  {label}
                </button>
              ))}
            </div>
            {range === 'month' && (
              <label>
                Month
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
              </label>
            )}
            {range === 'custom' && (
              <div className="field-row">
                <label>
                  From
                  <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label>
                  To
                  <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
                </label>
              </div>
            )}
          </>
        )}
        <label>
          Comments <span className="muted small">(optional, printed on the report)</span>
          <textarea value={comments} onChange={(e) => setComments(e.target.value)} maxLength={4000} rows={4} placeholder={`How ${child.name} is doing…`} />
        </label>
        <button className="btn primary" onClick={download} disabled={busy || bad}>
          <FileDown size={16} /> {busy ? 'Making it…' : 'Download Word document'}
        </button>
      </div>
    </Sheet>
  );
}

interface ReportPeriodView {
  name: string;
  start: string;
  end: string;
  available: boolean;
}

/** The assignment type whose work goes in the portfolio (#637). */
export const PORTFOLIO = 'Portfolio';

/**
 * A Portfolio-type assignment's work (#637): photos and PDFs the parent adds, straight into the portfolio. A new
 * assignment has nowhere to keep them yet, so it says to add it first.
 */
export function PortfolioWork({ child, plannerItemId, assignmentId }: { child: Child; plannerItemId?: string; assignmentId?: string }) {
  if (!childHas(child, 'portfolio')) return null;
  if (!plannerItemId && !assignmentId)
    return (
      <div className="stack work-samples portfolio-work">
        <strong>Work for the portfolio</strong>
        <span className="muted small">Add the assignment, then open it again to add photos or a PDF of the work. They go straight into the portfolio.</span>
      </div>
    );
  return <WorkSamples child={child} plannerItemId={plannerItemId} assignmentId={assignmentId} portfolio />;
}

/** The assignment category picker (#307): none, or one of the categories in alphabetical order. */
export function CategorySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">No category</option>
      {ASSIGNMENT_CATEGORIES.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

/** Edit the family's grading scale (#300): letters and the lowest percentage for each. */
function ScaleSheet({ child, onClose }: { child: Child; onClose: () => void }) {
  const { refresh, toast } = useParent();
  const [bands, setBands] = useState<GradeBand[]>(() => (child.gradingScale?.length ? child.gradingScale : DEFAULT_SCALE).map((b) => ({ ...b })));
  const [busy, setBusy] = useState(false);
  const update = (i: number, patch: Partial<GradeBand>) => setBands(bands.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const sorted = [...bands].sort((a, b) => b.min - a.min);
  const dupes = new Set(bands.map((b) => b.min)).size !== bands.length;

  async function save(next: GradeBand[] | null) {
    setBusy(true);
    try {
      await api.put('/api/grading-scale', { bands: next });
      await refresh();
      toast({ text: next ? 'Grading scale saved.' : 'Grading scale reset to A–F.', tone: 'good' });
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Grading scale" onClose={onClose}>
      <div className="stack">
        <p className="muted small">
          The lowest percentage for each letter. It’s used for every child’s grade book, the Student Portal and report cards. Anything below your lowest
          letter gets the bottom letter.
        </p>
        <div className="scale-list">
          {bands.map((b, i) => (
            <div key={i} className="scale-row">
              <input aria-label="Letter" value={b.letter} maxLength={3} onChange={(e) => update(i, { letter: e.target.value })} />
              <span className="muted small">from</span>
              <input
                aria-label="Lowest percentage"
                type="number"
                min={0}
                max={100}
                value={b.min}
                onChange={(e) => update(i, { min: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
              />
              <span className="muted small">%</span>
              <button className="icon-btn" onClick={() => setBands(bands.filter((_, j) => j !== i))} aria-label={`Remove ${b.letter}`} disabled={bands.length <= 1}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button className="btn small" onClick={() => setBands([...bands, { letter: '', min: 0 }])} disabled={bands.length >= 15}>
          <Plus size={15} /> Add a letter
        </button>
        {dupes && <p className="notice">Each letter needs a different lowest percentage.</p>}
        <div className="row-between">
          <button className="btn danger-text" disabled={busy} onClick={() => save(null)}>
            Reset to A–F
          </button>
          <button className="btn primary" disabled={busy || dupes || sorted.some((b) => !b.letter.trim())} onClick={() => save(sorted.map((b) => ({ letter: b.letter.trim(), min: b.min })))}>
            Save scale
          </button>
        </div>
      </div>
    </Sheet>
  );
}
